import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import type { RenderedPdfPage } from './receiptPdfAttachments'

export const isPdfFile = (file: File) => file.name.toLowerCase().endsWith('.pdf')

export const RECEIPT_SCREEN_ONLY_ATTR = 'data-receipt-screen-only'

const RECEIPT_IMAGE_MAX_PX = 1600
const RECEIPT_IMAGES_PER_ROW = 2

type ReceiptImage = { dataUrl: string; width: number; height: number }

const loadReceiptImage = (file: File) =>
	new Promise<ReceiptImage>((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const img = new Image()
		img.onload = () => {
			const scale = Math.min(
				1,
				RECEIPT_IMAGE_MAX_PX / Math.max(img.naturalWidth, img.naturalHeight),
			)
			const canvas = document.createElement('canvas')
			canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
			canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
			const ctx = canvas.getContext('2d')
			URL.revokeObjectURL(url)
			if (!ctx) {
				reject(new Error('Canvas is not supported.'))
				return
			}
			ctx.fillStyle = '#ffffff'
			ctx.fillRect(0, 0, canvas.width, canvas.height)
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
			resolve({
				dataUrl: canvas.toDataURL('image/jpeg', 0.85),
				width: canvas.width,
				height: canvas.height,
			})
		}
		img.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error(`Could not read ${file.name}.`))
		}
		img.src = url
	})

const addReceiptImagePages = async (pdf: jsPDF, files: File[]) => {
	const margin = 12
	const gap = 6
	const rowGap = 6
	const headerHeight = 12
	const captionHeight = 7
	const maxImageHeight = 100
	const placeholderHeight = 40
	const pageWidth = pdf.internal.pageSize.getWidth()
	const pageHeight = pdf.internal.pageSize.getHeight()
	const cellWidth = (pageWidth - margin * 2 - gap) / 2
	const contentTop = margin + headerHeight

	const items = await Promise.all(
		files.map(async (file) => {
			try {
				const image = await loadReceiptImage(file)
				const ratio = Math.min(cellWidth / image.width, maxImageHeight / image.height)
				return { file, image, width: image.width * ratio, height: image.height * ratio }
			} catch {
				return { file, image: null, width: cellWidth, height: placeholderHeight }
			}
		}),
	)

	const firstPage = pdf.getNumberOfPages() + 1
	let y = pageHeight

	for (let i = 0; i < items.length; i += RECEIPT_IMAGES_PER_ROW) {
		const row = items.slice(i, i + RECEIPT_IMAGES_PER_ROW)
		const rowHeight = Math.max(...row.map((item) => item.height)) + captionHeight
		if (y + rowHeight > pageHeight - margin) {
			pdf.addPage()
			y = contentTop
		}

		row.forEach((item, col) => {
			const cellX = margin + col * (cellWidth + gap)
			pdf.setFont('helvetica', 'normal')
			pdf.setFontSize(9)
			pdf.setTextColor(90)
			if (item.image) {
				pdf.addImage(
					item.image.dataUrl,
					'JPEG',
					cellX + (cellWidth - item.width) / 2,
					y,
					item.width,
					item.height,
				)
			} else {
				pdf.setDrawColor(200)
				pdf.rect(cellX, y, cellWidth, item.height)
				pdf.text('Preview unavailable', cellX + cellWidth / 2, y + item.height / 2, {
					align: 'center',
				})
			}
			const caption: string[] = pdf.splitTextToSize(item.file.name, cellWidth)
			pdf.text(caption[0] ?? '', cellX + cellWidth / 2, y + item.height + 5, { align: 'center' })
		})

		y += rowHeight + rowGap
	}

	const totalPages = pdf.getNumberOfPages() - firstPage + 1
	for (let page = 0; page < totalPages; page++) {
		pdf.setPage(firstPage + page)
		pdf.setFont('helvetica', 'bold')
		pdf.setFontSize(14)
		pdf.setTextColor(33)
		const pageLabel = totalPages > 1 ? ` (Page ${page + 1} of ${totalPages})` : ''
		pdf.text(`Attached Images${pageLabel}`, margin, margin + 6)
	}
}

const addReceiptPdfPages = async (pdf: jsPDF, files: File[]): Promise<string[]> => {
	const margin = 12
	const headerHeight = 12
	const captionHeight = 7
	const pageWidth = pdf.internal.pageSize.getWidth()
	const pageHeight = pdf.internal.pageSize.getHeight()
	const contentTop = margin + headerHeight
	const maxWidth = pageWidth - margin * 2
	const maxHeight = pageHeight - contentTop - margin - captionHeight

	let renderPdfPagesAsImages: (file: File) => Promise<RenderedPdfPage[]>
	try {
		;({ renderPdfPagesAsImages } = await import('./receiptPdfAttachments'))
	} catch (error) {
		console.error('Could not load the PDF renderer:', error)
		return files.map((file) => file.name)
	}

	const skipped: string[] = []
	for (let index = 0; index < files.length; index++) {
		const file = files[index]
		let pages: RenderedPdfPage[]
		try {
			pages = await renderPdfPagesAsImages(file)
		} catch (error) {
			console.error(`Could not add ${file.name} to the receipt:`, error)
			skipped.push(file.name)
			continue
		}

		pages.forEach((page, pageIndex) => {
			pdf.addPage()
			pdf.setFont('helvetica', 'bold')
			pdf.setFontSize(14)
			pdf.setTextColor(33)
			const pageLabel = pages.length > 1 ? ` (Page ${pageIndex + 1} of ${pages.length})` : ''
			pdf.text(`Attached PDF ${index + 1} of ${files.length}${pageLabel}`, margin, margin + 6)

			const ratio = Math.min(maxWidth / page.width, maxHeight / page.height)
			const width = page.width * ratio
			const height = page.height * ratio
			const x = margin + (maxWidth - width) / 2
			pdf.addImage(page.dataUrl, 'JPEG', x, contentTop, width, height)
			pdf.setDrawColor(200)
			pdf.rect(x, contentTop, width, height)

			pdf.setFont('helvetica', 'normal')
			pdf.setFontSize(9)
			pdf.setTextColor(90)
			const caption: string[] = pdf.splitTextToSize(file.name, maxWidth)
			pdf.text(caption[0] ?? '', pageWidth / 2, contentTop + height + 5, { align: 'center' })
		})
	}
	return skipped
}

export type ScaffoldReceiptDownloadResult = {
	skippedPdfNames: string[]
}

export const downloadScaffoldReceiptPdf = async (
	receiptElement: HTMLElement,
	requestId: number | undefined,
	imageFiles: File[],
	pdfFiles: File[] = [],
): Promise<ScaffoldReceiptDownloadResult> => {
	const screenOnly = receiptElement.querySelector(`[${RECEIPT_SCREEN_ONLY_ATTR}]`)
	const screenOnlyHeight = screenOnly?.getBoundingClientRect().height ?? 0
	const canvas = await html2canvas(receiptElement, {
		scale: 1.2,
		useCORS: true,
		allowTaint: true,
		backgroundColor: '#ffffff',
		width: receiptElement.scrollWidth,
		height: receiptElement.scrollHeight - screenOnlyHeight,
		ignoreElements: (element) => {
			return (
				element.hasAttribute(RECEIPT_SCREEN_ONLY_ATTR) ||
				(element.classList.contains('MuiButton-root') &&
					(element.textContent?.includes('Download Receipt') ?? false))
			)
		},
	})

	const imgData = canvas.toDataURL('image/png')
	const pdf = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
		format: 'a4',
	})

	const pdfWidth = pdf.internal.pageSize.getWidth()
	const pdfHeight = pdf.internal.pageSize.getHeight()
	const ratio = Math.min(pdfWidth / canvas.width, pdfHeight / canvas.height)
	const scaledWidth = canvas.width * ratio
	const scaledHeight = canvas.height * ratio

	pdf.addImage(imgData, 'PNG', (pdfWidth - scaledWidth) / 2, 0, scaledWidth, scaledHeight)

	if (imageFiles.length > 0) {
		await addReceiptImagePages(pdf, imageFiles)
	}

	const skippedPdfNames = pdfFiles.length > 0 ? await addReceiptPdfPages(pdf, pdfFiles) : []

	pdf.save(`scaffold-request-${requestId}-receipt.pdf`)
	return { skippedPdfNames }
}
